/* RESPONSIBLE TEAM: team-inbox-management */
import moment from 'moment-timezone';

export default class TeammatesCsvExporter {
  constructor(
    app,
    admins,
    {
      workspaceAssignmentLimit,
      workspaceTicketAssignmentLimit,
      workspaceChannelLimits,
      teamInboxes,
      teamSettings,
      intl,
    },
  ) {
    this.app = app;
    this.admins = admins;
    this.workspaceAssignmentLimit = workspaceAssignmentLimit;
    this.workspaceTicketAssignmentLimit = workspaceTicketAssignmentLimit;
    this.workspaceChannelLimits = workspaceChannelLimits;

    this.teamInboxes = teamInboxes;
    this.teamSettings = teamSettings;
    this.intl = intl;
  }

  get filename() {
    return this.intl.t('settings.teammate-workload.export-csv.filename', {
      date: moment.tz(this.app.timezone).format('YYYY-MM-DD'),
    });
  }

  get data() {
    let data = this.admins.map((admin) => this.row(admin));
    data.unshift(this.headerColumn);

    return data;
  }

  row(admin) {
    let baseColumns = [
      this.name(admin),
      this.email(admin),
      this.role(admin),
      this.assignmentLimit(admin),
    ];

    baseColumns.push(this.ticketAssignmentLimit(admin));

    if (this.app.canUseChannelAssignmentLimits) {
      baseColumns.push(this.emailChannelLimit(admin), this.messengerChannelLimit(admin));
    }

    baseColumns.push(this.primaryInboxes(admin), this.secondaryInboxes(admin));

    return baseColumns;
  }

  name(admin) {
    return admin.name;
  }

  email(admin) {
    return admin.email;
  }

  role(admin) {
    return (
      admin.currentAppPermissions?.role?.name ||
      this.intl.t('settings.teammate-workload.export-csv.no-role')
    );
  }

  assignmentLimit(admin) {
    return (
      admin.workload_management_settings?.assignment_limit?.toString() ||
      `${this.workspaceAssignmentLimit} (${this.intl.t('settings.teammate-workload.export-csv.workspace-conversation-limit')})` ||
      this.intl.t('settings.teammate-workload.export-csv.no-limit')
    );
  }

  ticketAssignmentLimit(admin) {
    return (
      admin.workload_management_settings?.ticket_assignment_limit?.toString() ||
      `${this.workspaceTicketAssignmentLimit} (${this.intl.t(
        'settings.teammate-workload.export-csv.workspace-ticket-limit',
      )})` ||
      this.intl.t('settings.teammate-workload.export-csv.no-limit')
    );
  }

  emailChannelLimit(admin) {
    let teammateLimit = admin.workload_management_settings?.channel_limits?.['email'];
    if (teammateLimit != null) {
      return teammateLimit.toString();
    }
    let workspaceDefault = this.workspaceChannelLimits?.['email'];
    if (workspaceDefault != null) {
      return `${workspaceDefault} (${this.intl.t('settings.teammate-workload.export-csv.workspace-email-limit')})`;
    }
    return '-';
  }

  messengerChannelLimit(admin) {
    let teammateLimit = admin.workload_management_settings?.channel_limits?.['messenger'];
    if (teammateLimit != null) {
      return teammateLimit.toString();
    }
    let workspaceDefault = this.workspaceChannelLimits?.['messenger'];
    if (workspaceDefault != null) {
      return `${workspaceDefault} (${this.intl.t('settings.teammate-workload.export-csv.workspace-messenger-limit')})`;
    }
    return '-';
  }

  primaryInboxes(admin) {
    return this.teamInboxes
      .filter((inbox) => inbox.primaryMembers.includes(parseInt(admin.id, 10)))
      .map((inbox) => inbox.name)
      .join(', ');
  }

  secondaryInboxes(admin) {
    return this.teamInboxes
      .filter((inbox) => this.teamSettingByTeamId(inbox.id)?.distributionMethod === 'load_balanced')
      .filter((inbox) => inbox.secondaryMembers.includes(parseInt(admin.id, 10)))
      .map((inbox) => inbox.name)
      .join(', ');
  }

  teamSettingByTeamId(teamId) {
    return this.teamSettings.find((teamSetting) => teamSetting.teamId === teamId);
  }

  get headerColumn() {
    let headers = [
      this.intl.t('settings.teammate-workload.export-csv.name'),
      this.intl.t('settings.teammate-workload.export-csv.email'),
      this.intl.t('settings.teammate-workload.export-csv.role'),
    ];

    headers.push(
      this.intl.t('settings.teammate-workload.export-csv.conversation-assignment-limit'),
      this.intl.t('settings.teammate-workload.export-csv.ticket-assignment-limit'),
    );

    if (this.app.canUseChannelAssignmentLimits) {
      headers.push(
        this.intl.t('settings.teammate-workload.export-csv.email-channel-limit'),
        this.intl.t('settings.teammate-workload.export-csv.messenger-channel-limit'),
      );
    }

    headers.push(
      this.intl.t('settings.teammate-workload.export-csv.primary-inboxes'),
      this.intl.t('settings.teammate-workload.export-csv.secondary-inboxes'),
    );

    return headers;
  }
}
